import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  BookOpen,
  Award,
  TrendingUp,
  CheckCircle2,
  Clock,
  Circle,
  ChevronDown,
  X,
} from "lucide-react";
import Nav from "../Components/Nav/Nav";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import Login from "./Login";
import ImageProfil from "../Components/Profil/ImageProfil";
import { getFormationStats } from "../actions/formations.actions";

// Classe de couleur d'une barre/pastille selon le taux (rouge → ambre → vert).
const pctTone = (p) => (p >= 75 ? "high" : p >= 40 ? "mid" : "low");

const statusMeta = {
  completed: { Icon: CheckCircle2, label: "Terminé", cls: "completed" },
  in_progress: { Icon: Clock, label: "En cours", cls: "in_progress" },
  not_started: { Icon: Circle, label: "Pas commencé", cls: "not_started" },
};

const AdminFormationStats = () => {
  const userData = useSelector((s) => s.userReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // Membre dont on affiche le détail dans la modale ({ member, teamLabel }).
  const [detail, setDetail] = useState(null);
  // Équipes dépliées (repliées par défaut → objet vide).
  const [openTeams, setOpenTeams] = useState({});

  const toggleTeam = (id) => setOpenTeams((o) => ({ ...o, [id]: !o[id] }));

  const isAllowed = userData.isAdmin || userData.isReferent;

  useEffect(() => {
    dispatch(getFormationStats())
      .then(setData)
      .catch(() => setError(true));
  }, [dispatch]);

  if (!userData || !userData.id || !isAllowed) return <Login />;

  const global = data?.global;
  const teams = Array.isArray(data?.teams) ? data.teams : [];

  return (
    <>
      <Nav />
      <div className="mainContainer">
        <AdminMenu />
        <div className="centerBlock_admin">
          <AdminFormHeader
            title="Statistiques formations"
            subtitle="Taux de complétion global et progression par équipe."
            back={
              <button className="mini-btn" onClick={() => navigate("/admin/formations")}>
                <ArrowLeft size={14} /> Retour
              </button>
            }
          />

          {error ? (
            <p className="builder-empty">Impossible de charger les statistiques.</p>
          ) : !data ? (
            <p className="builder-empty">Chargement…</p>
          ) : (
            <div className="formation-stats">
              {/* Bandeau global */}
              <div className="stats-overview">
                <div className={`stats-global tone-${pctTone(global.percent)}`}>
                  <div className="stats-global-ring">
                    <TrendingUp size={20} />
                    <span className="stats-global-pct">{global.percent}%</span>
                  </div>
                  <div className="stats-global-text">
                    <p className="stats-global-title">Taux de complétion global</p>
                    <span className="stats-global-sub">
                      {global.completedPairs} formation
                      {global.completedPairs > 1 ? "s" : ""} validée
                      {global.completedPairs > 1 ? "s" : ""} sur {global.totalPairs} attendue
                      {global.totalPairs > 1 ? "s" : ""}
                    </span>
                    <span className="stats-global-hint">
                      {global.totalUsers} apprenant{global.totalUsers > 1 ? "s" : ""} ×{" "}
                      {global.totalPublished} formation{global.totalPublished > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div className="stats-kpis">
                  <div className="stats-kpi">
                    <Users size={18} />
                    <div>
                      <strong>{global.totalUsers}</strong>
                      <span>Utilisateurs actifs</span>
                    </div>
                  </div>
                  <div className="stats-kpi">
                    <BookOpen size={18} />
                    <div>
                      <strong>{global.totalPublished}</strong>
                      <span>Formations publiées</span>
                    </div>
                  </div>
                  <div className="stats-kpi">
                    <Award size={18} />
                    <div>
                      <strong>{global.totalBadges}</strong>
                      <span>Badges obtenus</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards par équipe */}
              {teams.length === 0 ? (
                <div className="results-empty">
                  <Users size={40} />
                  <p>Aucune équipe à afficher.</p>
                </div>
              ) : (
                <div className="team-cards">
                  {teams.map((team) => {
                    const open = !!openTeams[team.id];
                    return (
                    <div className={`team-card ${open ? "open" : ""}`} key={team.id}>
                      <div
                        className="team-card-head"
                        onClick={() => toggleTeam(team.id)}
                        role="button"
                      >
                        <div className="team-coach">
                          {team.coach ? (
                            <ImageProfil id={team.coach.id} />
                          ) : (
                            <span className="team-coach-avatar none">
                              <Users size={18} />
                            </span>
                          )}
                          <div>
                            <p className="team-coach-name">{team.label}</p>
                            <span className="team-coach-role">
                              {team.coach ? "Coach · " : ""}
                              {team.memberCount} membre{team.memberCount > 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        <div className="team-head-right">
                          <div className={`team-pct tone-${pctTone(team.percent)}`}>
                            <span>{team.percent}%</span>
                            <small>complétude moyenne</small>
                          </div>
                          <ChevronDown
                            size={20}
                            className={`team-chevron ${open ? "open" : ""}`}
                          />
                        </div>
                      </div>

                      {open && (
                      <div className="team-members">
                        {team.members.map((m) => (
                          <div
                            className={`member-row ${m.isCoach ? "is-coach" : ""}`}
                            key={m.user.id}
                          >
                            <div className="member-id">
                              <ImageProfil id={m.user.id} />
                              <div className="member-id-text">
                                <p className="member-name">
                                  {m.user.firstname} {m.user.name}
                                  {m.isCoach && <span className="member-coach-tag">Coach</span>}
                                </p>
                                <span className="member-count">
                                  {m.completedCount}/{m.totalPublished} formation
                                  {m.totalPublished > 1 ? "s" : ""}
                                </span>
                              </div>
                            </div>

                            <div className="member-progress">
                              <div className={`bar tone-${pctTone(m.percent)}`}>
                                <span style={{ width: `${m.percent}%` }} />
                              </div>
                              <span className="member-pct">{m.percent}%</span>
                            </div>

                            <button
                              className="mini-btn"
                              onClick={() => setDetail({ member: m, teamLabel: team.label })}
                            >
                              Détails
                            </button>
                          </div>
                        ))}
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {detail &&
        createPortal(
          <div
            className="fsm-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setDetail(null);
            }}
          >
            <div className="fsm-modal" role="dialog" aria-label="Détail des formations du membre">
              <div className="fsm-head">
                <ImageProfil id={detail.member.user.id} />
                <div className="fsm-head-text">
                  <p className="fsm-name">
                    {detail.member.user.firstname} {detail.member.user.name}
                  </p>
                  <span className="fsm-sub">
                    {detail.teamLabel} · {detail.member.completedCount}/
                    {detail.member.totalPublished} formation
                    {detail.member.totalPublished > 1 ? "s" : ""} terminée
                    {detail.member.completedCount > 1 ? "s" : ""}
                  </span>
                </div>
                <span className={`fsm-pct tone-${pctTone(detail.member.percent)}`}>
                  {detail.member.percent}%
                </span>
                <button className="fsm-close" onClick={() => setDetail(null)} aria-label="Fermer">
                  <X size={18} />
                </button>
              </div>

              <div className="fsm-body">
                {detail.member.formations.length === 0 ? (
                  <p className="fsm-empty">Aucune formation publiée.</p>
                ) : (
                  detail.member.formations.map((f) => {
                    const sm = statusMeta[f.status] || statusMeta.not_started;
                    const SIcon = sm.Icon;
                    return (
                      <div className="fsm-row" key={f.formationId}>
                        <span className="fsm-row-title">{f.title}</span>
                        <div className={`fsm-bar tone-${pctTone(f.percent)}`}>
                          <span style={{ width: `${f.percent}%` }} />
                        </div>
                        <span className="fsm-row-pct">{f.percent}%</span>
                        <span className={`fsm-status ${sm.cls}`}>
                          <SIcon size={13} /> {sm.label}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AdminFormationStats;
